/**
 * LEVANTAMIENTO DE VIDEOVIGILANCIA — la guía, el plano y el resultado.
 * =============================================================================
 *
 * Tercer módulo del levantamiento guiado, con la misma estructura que riego y eléctrico:
 * guía al lado del dibujo, teoría detrás de un toque, y todo número con su porqué.
 *
 * **Lo propio de este rubro, y por qué la pantalla está armada así:**
 *
 * El cliente pide «cámaras» y lo que compra es un NIVEL DE DETALLE. Por eso el paso 2 —
 * acordar qué hay que poder ver en cada zona— ocurre ANTES de dibujar, y por eso el
 * resultado no reporta «cobertura» a secas sino los cuatro niveles a la vez. «Tu jardín
 * queda detectado al 100% pero identificado sólo en la puerta» es una frase que sólo se
 * puede decir si el sistema la calcula, y es la que evita el reclamo de después.
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import PasoGuia, { Avance } from './PasoGuia.jsx'
import Materiales from './Materiales.jsx'
import Recinto from './Recinto.jsx'
import { GUIA_CAMARAS } from '../motor/guia.js'
import { DORI, CAMARAS, camaraPorId, alcanceDe, calcular, LIMITE_UTP } from '../motor/camaras.js'

/** Qué modo del lienzo corresponde a cada paso de la guía. */
const MODO_POR_PASO = {
  recorrido: 'ver', nivel: 'ver', trazo: 'trazo',
  rack: 'rack', colocar: 'colocar', calcular: 'ver'
}

export default function Camaras ({ atras, onGuardar, insumos = [], preferencias = {}, plano = null, onPlano, semilla = [] }) {
  const [i, setI] = useState(0)

  /* EL PREDIO VIVE EN LA CASA (escalón 9a, 2026-08-20): si ya estaba trazado, el
     levantamiento arranca con él y con el rack ubicado; cada cierre del trazo se
     devuelve a la casa. Ver la misma nota en Riego.jsx. */
  const [poligono, setPoligono] = useState(plano?.poligono ?? [])
  const [cerrado, setCerrado] = useState(!!plano?.cerrado)
  const [camaras, setCamaras] = useState(semilla)     // las colocadas en el plano (E7-lite)
  const [rack, setRack] = useState(plano?.rack ?? null)
  const heredado = !!(plano?.poligono?.length >= 3)
  const ultimoPlano = useRef(JSON.stringify(plano || null))
  useEffect(() => {
    if (!onPlano || !cerrado || poligono.length < 3) return
    const sig = { poligono, cerrado, rack }
    const clave = JSON.stringify(sig)
    if (clave === ultimoPlano.current) return
    ultimoPlano.current = clave
    onPlano(sig)
  }, [poligono, cerrado, rack, onPlano])
  const [modelo, setModelo] = useState('hik-2143g2-4')
  const [seleccion, setSeleccion] = useState(null)

  const [nivelObjetivo, setNivelObjetivo] = useState('reconocimiento')
  const [dias, setDias] = useState('15')
  const [notas, setNotas] = useState('')

  const paso = GUIA_CAMARAS.pasos[i]
  const modo = MODO_POR_PASO[paso.id] || 'ver'

  const r = useMemo(() => {
    if (!cerrado || poligono.length < 3 || !camaras.length || !rack) return null
    return calcular({
      recinto: poligono, camaras, rack, nivelObjetivo,
      dias: parseInt(dias) || 15,
      insumos,
      holguraObra: preferencias.holguraObra ?? 1.10
    })
  }, [poligono, camaras, rack, cerrado, nivelObjetivo, dias, insumos, preferencias.holguraObra])

  const falta = (() => {
    if (paso.id === 'trazo' && poligono.length < 3) return 'Marca al menos tres esquinas.'
    if (paso.id === 'trazo' && !cerrado) return 'Cierra el trazo para continuar.'
    if (paso.id === 'rack' && !rack) return 'Toca el plano donde va el grabador.'
    if (paso.id === 'colocar' && !camaras.length) return 'Coloca al menos una cámara.'
    return null
  })()

  const avanzar = () => setI(Math.min(i + 1, GUIA_CAMARAS.pasos.length - 1))
  const volver = () => setI(Math.max(i - 1, 0))

  const camSel = camaras.find(c => c.id === seleccion)

  return (
    <div className="app">
      <div className="bar">
        <button className="ib ico" onClick={atras} aria-label="Regresar">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <h1>Videovigilancia
          <span className="crumb">{GUIA_CAMARAS.duracion} · paso {i + 1} de {GUIA_CAMARAS.pasos.length}</span>
        </h1>
      </div>

      <main>
        <Avance pasos={GUIA_CAMARAS.pasos} i={i} onIr={setI} />
        <PasoGuia key={paso.id} paso={paso} />

        {/* ---------- Captura por paso ---------- */}

        {paso.id === 'nivel' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>
              ¿Qué nivel se acordó para la zona principal?
            </div>
            {/* Los cuatro niveles con su explicación, no sólo su nombre: quien usa esto no
                tiene por qué saber qué quiere decir «reconocimiento» en una norma. */}
            <div className="opciones">
              {DORI.map(n => (
                <button key={n.id} type="button"
                  className={`opcion ${nivelObjetivo === n.id ? 'on' : ''}`}
                  onClick={() => setNivelObjetivo(n.id)}>
                  <b>{n.nombre}</b>
                  <span className="s">{n.que}</span>
                  <span className="s">{n.para}</span>
                </button>
              ))}
            </div>
            <div className="nota acento" style={{ marginTop: 'var(--e4)' }}>
              Con la cámara de 4 mm, <b>identificar</b> alcanza hasta{' '}
              {camaraPorId('hik-2143g2-4').dori.identificacion} m y <b>detectar</b> hasta{' '}
              {camaraPorId('hik-2143g2-4').dori.deteccion} m. Es la misma cámara: cambia
              cuánto se le puede pedir a cada distancia.
            </div>
          </div>
        )}

        {paso.id === 'trazo' && heredado && (
          <div className="nota acento">
            <b>El predio ya estaba trazado en esta casa.</b> Viene de un levantamiento
            anterior: revísalo, corrige una esquina si cambió algo, o vuelve a trazarlo.
          </div>
        )}
        {paso.id === 'trazo' && poligono.length >= 3 && !cerrado && (
          <div className="captura">
            <button className="ib pri w" onClick={() => setCerrado(true)}>
              Cerrar el trazo
            </button>
          </div>
        )}

        {paso.id === 'colocar' && (
          <div className="captura">
            <div className="sec" style={{ margin: '0 0 var(--e3)' }}>Qué cámara vas a poner</div>
            <div className="opciones">
              {CAMARAS.map(c => (
                <button key={c.id} type="button"
                  className={`opcion ${modelo === c.id ? 'on' : ''}`}
                  onClick={() => setModelo(c.id)}>
                  <b>{c.lente} · {c.mp} MP</b>
                  <span className="s">
                    Identifica a {c.dori.identificacion} m · detecta a {c.dori.deteccion} m
                  </span>
                  <span className="s">{c.para}</span>
                </button>
              ))}
            </div>

            {/* La cámara seleccionada, con sus cuatro alcances. Es lo que convierte el
                arrastre en una decisión informada en vez de un tanteo. */}
            {camSel && (() => {
              const cam = camaraPorId(camSel.modelo)
              return (
                <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
                  <b>Cámara seleccionada:</b> {cam.lente}, apuntando a {camSel.rumbo}°.
                  <ul style={{ margin: 'var(--e2) 0 0', paddingLeft: '1.1rem' }}>
                    {DORI.map(n => (
                      <li key={n.id}>
                        {n.nombre} hasta <b>{alcanceDe(cam, n.id).metros} m</b>
                      </li>
                    ))}
                  </ul>
                  <button className="ib sm" style={{ marginTop: 'var(--e3)' }}
                    onClick={() => { setCamaras(camaras.filter(c => c.id !== seleccion)); setSeleccion(null) }}>
                    Quitar esta cámara
                  </button>
                </div>
              )
            })()}
          </div>
        )}

        {paso.id === 'calcular' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Días de grabación que quiere conservar</span>
              <input type="number" inputMode="numeric" value={dias}
                onChange={e => setDias(e.target.value)} />
              <span>Es lo que decide el tamaño del disco. Quince días es lo habitual en
                    residencial; treinta si hay servicio de personal rotativo.</span>
            </label>
          </div>
        )}

        {/* ---------- El plano ---------- */}
        <Recinto
          poligono={poligono} setPoligono={setPoligono}
          camaras={camaras} setCamaras={setCamaras}
          rack={rack} setRack={setRack}
          cerrado={cerrado} setCerrado={setCerrado}
          modo={modo} modeloActivo={modelo}
          nivelObjetivo={nivelObjetivo}
          seleccion={seleccion} setSeleccion={setSeleccion} />

        {paso.id === 'recorrido' && (
          <div className="captura">
            <label className="f" style={{ marginBottom: 0 }}>
              <span>Lo que preocupa al cliente, y lo que viste</span>
              <textarea rows="3" value={notas} onChange={e => setNotas(e.target.value)}
                placeholder="Ej. Le preocupa la reja de la cochera. La barda del fondo da a un lote baldío y no tiene luz. Techo del vecino permite saltar al patio de servicio." />
            </label>
          </div>
        )}

        {paso.id === 'calcular' && (
          r ? <Resultado r={r} notas={notas} onGuardar={onGuardar} />
            : <div className="empty">
                <b>Todavía no se puede calcular.</b>
                <p>Falta cerrar el trazo, ubicar el grabador o colocar al menos una cámara.</p>
              </div>
        )}
      </main>

      <div className="fab"><div className="in">
        <button className="gh" onClick={volver} disabled={i === 0} aria-label="Paso anterior">←</button>
        <button onClick={avanzar} disabled={!!falta || i === GUIA_CAMARAS.pasos.length - 1}>
          {falta || (i === GUIA_CAMARAS.pasos.length - 1 ? 'Listo' : 'Siguiente')}
        </button>
      </div></div>
    </div>
  )
}

function Resultado ({ r, notas, onGuardar }) {
  const [auditando, setAuditando] = useState(false)

  return (
    <>
      <div className="sec">Lo que va a ver el cliente</div>

      {/* LOS CUATRO NIVELES A LA VEZ. Es el corazón de la pantalla: reportar sólo el nivel
          objetivo escondería que en el resto del predio se ve mucho menos, que es
          exactamente el malentendido que este módulo existe para evitar. */}
      <div className="card">
        {r.porNivel.map(n => (
          <div className="ln" key={n.id}>
            <span>{n.nombre}<span className="s" style={{ display: 'block' }}>{n.que}</span></span>
            <b className={n.id === r.nivelObjetivo ? 'grande' : undefined}>
              {n.porcentaje.toFixed(0)}%
            </b>
          </div>
        ))}
      </div>
      <div className="nota leible">
        El porcentaje es de la superficie del predio. Se acordó llegar a{' '}
        <b>{r.porNivel.find(n => n.id === r.nivelObjetivo)?.nombre.toLowerCase()}</b>, y ahí
        queda el <b>{r.cobertura.porcentaje.toFixed(0)}%</b>. Lo que sobra del nivel de
        abajo no es un extra: es lo que <i>no</i> alcanza el nivel acordado.
      </div>

      <div className="sec">Instalación</div>
      <div className="ln"><span>Cámaras</span><b>{r.camaras.length}</b></div>
      <div className="ln"><span>Cable UTP</span><b>{r.red.metros} m</b></div>
      <div className="ln">
        <span>Consumo PoE</span><b>{r.poe.consumo.toFixed(1)} W</b>
      </div>
      <div className="ln">
        <span>Grabación</span>
        <b>{r.disco.tbComercial} TB · {r.disco.dias} días</b>
      </div>

      {/* El límite del cobre. No es un detalle: es lo que hace que un enlace no levante
          el día de la instalación, y se descubre en obra si no se dice aquí. */}
      {r.red.fueraDeLimite > 0 && (
        <div className="warn leible" style={{ marginTop: 'var(--e4)' }}>
          <b>{r.red.fueraDeLimite} {r.red.fueraDeLimite === 1 ? 'cámara queda' : 'cámaras quedan'} a
          más de {LIMITE_UTP.canal} m del grabador.</b> El cobre no llega más lejos, y
          alargar el cable no es opción: el enlace simplemente no levanta. Hay que meter un
          switch intermedio o pasar ese tramo a fibra, y eso se cotiza aparte.
          <ul style={{ margin: 'var(--e3) 0 0', paddingLeft: '1.1rem' }}>
            {r.red.tramos.filter(t => t.excedeLimite).map((t, n) => (
              <li key={n}>Cámara {t.camara + 1}: {t.metros} m</li>
            ))}
          </ul>
        </div>
      )}

      <div className="sec">Material</div>
      <Materiales lista={r.materiales} auditando={auditando} />

      <button type="button" className="discreto" style={{ marginTop: 'var(--e5)' }}
        onClick={() => setAuditando(!auditando)} aria-expanded={auditando}>
        {auditando ? 'Ocultar de dónde sale cada número' : 'Ver de dónde sale cada número'}
      </button>

      {auditando && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <p style={{ marginTop: 0 }}>
            <b>Niveles de detalle.</b> Son los cuatro de la norma <b>IEC 62676-4</b>, medidos
            en píxeles por metro sobre el objetivo: detección 25, observación 62.5,
            reconocimiento 125 e identificación 250. No es la resolución de la cámara: la
            misma cámara identifica de cerca y apenas detecta de lejos.
          </p>
          <p>
            <b>Alcances.</b> Salen de la <b>ficha del fabricante</b>, que publica la distancia
            de cada nivel para cada distancia focal. Se prefiere la ficha sobre el modelo
            geométrico porque el modelo da distancias más cortas y no se pudo reproducir la
            cuenta del fabricante; cuando una cámara no publica DORI, se usa el modelo y se
            declara.
          </p>
          <p>
            <b>Cable.</b> Un tramo por cámara desde el grabador, por trayectoria ortogonal —
            que es como se canaliza— más la holgura de obra. Cada cámara necesita su propio
            enlace: no se pueden compartir tramos como en la tubería de riego.
          </p>
          <p style={{ marginBottom: 0 }}>
            <b>Disco.</b> Mbps × cámaras × segundos del periodo, entre 8 para pasar a bytes.
            El bitrate es un valor de trabajo, no de ficha: la ficha publica un rango de
            32 Kbps a 8 Mbps según la escena.
          </p>
        </div>
      )}

      {notas && (
        <div className="nota leible" style={{ marginTop: 'var(--e4)' }}>
          <b>Del recorrido:</b> {notas}
        </div>
      )}

      {onGuardar && (
        <button className="ib pri w" style={{ marginTop: 'var(--e6)' }}
          onClick={() => onGuardar(r)}>Agregar a la cotización</button>
      )}
    </>
  )
}
